/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { motion } from "framer-motion";
import AppModalShell, {
  AppModalContentItem,
} from "../common/AppModalShell";

const KEYBOARD_OPEN_THRESHOLD_PX = 120;

const DirectChatBlockModal = ({
  open,
  onClose,
  category,
  onCategoryChange,
  comment,
  onCommentChange,
  onSubmit,
  submitting,
  t,
  mode = "user",
  title,
  subtitle,
}) => {
  const baselineViewportHeightRef = React.useRef(0);
  const [keyboardViewport, setKeyboardViewport] = React.useState({
    open: false,
    top: 0,
    height: 0,
  });
  const reportCategories = [
    "harassment",
    "unwanted",
    "offensive",
    "other",
  ];
  const isMessageReport = mode === "message";

  React.useEffect(() => {
    if (!open || typeof window === "undefined") {
      baselineViewportHeightRef.current = 0;
      setKeyboardViewport({ open: false, top: 0, height: 0 });
      return undefined;
    }

    let rafId = null;
    const readViewport = () => {
      const vv = window.visualViewport;
      const layoutHeight = Math.max(
        0,
        Number(window.innerHeight || document.documentElement?.clientHeight || 0)
      );
      const visualHeight = Math.max(0, Number(vv?.height || layoutHeight));
      const visualWidth = Math.max(0, Number(vv?.width || window.innerWidth || 0));
      const visualTop = Math.max(0, Number(vv?.offsetTop || 0));

      baselineViewportHeightRef.current = Math.max(
        baselineViewportHeightRef.current,
        layoutHeight,
        visualHeight
      );

      const baselineHeight = baselineViewportHeightRef.current || visualHeight;
      const isMobileViewport = Math.min(
        visualWidth || window.innerWidth || 0,
        window.innerWidth || visualWidth || 0
      ) < 768;
      const keyboardOpen =
        isMobileViewport &&
        baselineHeight - visualHeight > KEYBOARD_OPEN_THRESHOLD_PX;

      setKeyboardViewport({
        open: keyboardOpen,
        top: keyboardOpen ? visualTop : 0,
        height: keyboardOpen ? visualHeight : 0,
      });
    };

    const scheduleViewportRead = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        readViewport();
      });
    };

    readViewport();
    const timerOne = window.setTimeout(readViewport, 80);
    const timerTwo = window.setTimeout(readViewport, 240);

    window.addEventListener("resize", scheduleViewportRead, { passive: true });
    window.addEventListener("orientationchange", scheduleViewportRead, { passive: true });
    window.visualViewport?.addEventListener?.("resize", scheduleViewportRead);
    window.visualViewport?.addEventListener?.("scroll", scheduleViewportRead);

    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      window.clearTimeout(timerOne);
      window.clearTimeout(timerTwo);
      window.removeEventListener("resize", scheduleViewportRead);
      window.removeEventListener("orientationchange", scheduleViewportRead);
      window.visualViewport?.removeEventListener?.("resize", scheduleViewportRead);
      window.visualViewport?.removeEventListener?.("scroll", scheduleViewportRead);
    };
  }, [open]);

  const keyboardRootStyle = keyboardViewport.open
    ? {
        top: `${keyboardViewport.top}px`,
        bottom: "auto",
        height: `${Math.max(320, keyboardViewport.height)}px`,
        alignItems: "flex-end",
        padding: "max(env(safe-area-inset-top, 0px), 8px) 14px 10px",
      }
    : null;
  const keyboardPanelStyle = keyboardViewport.open
    ? {
        maxHeight: `calc(${Math.max(
          320,
          keyboardViewport.height
        )}px - max(env(safe-area-inset-top, 0px), 8px) - 18px)`,
      }
    : null;
  const handleCommentFocus = (event) => {
    const field = event.currentTarget;
    window.setTimeout(() => {
      field?.scrollIntoView?.({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }, 140);
  };

  return (
    <AppModalShell
      open={open}
      title={title || t("chats.direct_chat.block_title")}
      onClose={onClose}
      closeAriaLabel={t("chats.direct_chat.block_close_aria")}
      zIndexClassName="z-[120]"
      maxWidthClassName="max-w-md"
      titleClassName="text-[18px] sm:text-2xl"
      bodyClassName="px-5 pb-5 pt-1 sm:px-6 sm:pb-6"
      viewportStyle={keyboardRootStyle}
      panelStyle={keyboardPanelStyle}
    >
      {!isMessageReport ? (
        <AppModalContentItem className="text-center text-[13px] font-semibold leading-[18px] text-white/80">
          {subtitle || t("chats.direct_chat.block_subtitle")}
        </AppModalContentItem>
      ) : null}

      {isMessageReport ? (
        <AppModalContentItem className="mt-4 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-center text-[13px] font-semibold leading-[19px] text-white/86">
          <p>{t("chats.direct_chat.message_report_body")}</p>
          <p className="mt-3 text-white/72">
            {t("chats.direct_chat.message_report_thanks")}
          </p>
        </AppModalContentItem>
      ) : (
        <>
          <AppModalContentItem className="mt-4 flex flex-col gap-2.5">
            {reportCategories.map((categoryCode) => {
              const isSelected = category === categoryCode;
              return (
                <button
                  key={categoryCode}
                  type="button"
                  onClick={() => onCategoryChange?.(categoryCode)}
                  className={`w-full rounded-2xl border px-4 py-3 text-left text-[14px] font-semibold transition ${
                    isSelected
                      ? "border-[#6DB7FF] bg-[#1A5EA8] text-white"
                      : "border-white/10 bg-white/10 text-white/90"
                  }`}
                >
                  {t(`chats.direct_chat.report_categories.${categoryCode}`)}
                </button>
              );
            })}
          </AppModalContentItem>

          <AppModalContentItem className="mt-4 text-white/80 text-[12px] font-semibold leading-[16px]">
            {t("chats.direct_chat.report_optional_label")}
          </AppModalContentItem>

          <AppModalContentItem
            as={motion.textarea}
            value={comment || ""}
            onChange={(e) => onCommentChange?.(e.target.value)}
            onFocus={handleCommentFocus}
            placeholder={t("chats.direct_chat.block_placeholder")}
            className="mt-2 w-full min-h-[104px] rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-white placeholder:text-white/40 outline-none resize-none"
          />
        </>
      )}

      <AppModalContentItem className="mt-5 flex gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 h-12 rounded-2xl bg-white/10 text-white font-semibold"
        >
          {t("chats.direct_chat.block_cancel")}
        </button>

        <button
          type="button"
          onClick={onSubmit}
          disabled={(!isMessageReport && !String(category || "").trim()) || submitting}
          className="flex-1 h-12 rounded-2xl bg-white/20 text-white font-semibold disabled:opacity-50 disabled:cursor-default flex items-center justify-center"
        >
          {submitting
            ? t("chats.direct_chat.block_submitting")
            : t("chats.direct_chat.block_confirm")}
        </button>
      </AppModalContentItem>
    </AppModalShell>
  );
};

export default DirectChatBlockModal;
